import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { format, parse, isValid, subDays } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import './Dashboard.css';

const DATE_DISPLAY = 'dd/MM/yyyy';
const SUBSCRIPTION_ACTION_SET_DATE = 'set-date';
const SUBSCRIPTION_ACTION_END_NOW = 'end-now';

function displayStringToDate(str) {
  const t = String(str ?? '').trim();
  if (!t) return null;
  const d = parse(t, DATE_DISPLAY, new Date());
  return isValid(d) ? d : null;
}

function Subscription() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [periodEndDate, setPeriodEndDate] = useState('');
  const [subscriptionAction, setSubscriptionAction] = useState(SUBSCRIPTION_ACTION_SET_DATE);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const debounceRef = useRef(null);

  const fetchStudents = useCallback(async (q) => {
    const trimmed = String(q).trim();
    if (!trimmed) {
      setResults([]);
      setSearchError('');
      return;
    }
    setSearchLoading(true);
    setSearchError('');
    try {
      const res = await api.get('/data/students/by-username', { params: { q: trimmed } });
      setResults(res.data.students || []);
    } catch (err) {
      setResults([]);
      setSearchError(
        err.response?.data?.error || err.response?.data?.details || 'Failed to search students'
      );
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setSearchError('');
      return () => {};
    }
    debounceRef.current = setTimeout(() => {
      fetchStudents(trimmed);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchStudents]);

  const addStudent = (row) => {
    setSelectedStudents((prev) => {
      if (prev.some((p) => p.student_id === row.student_id)) return prev;
      return [...prev, { student_id: row.student_id, username: row.username, plan_id: row.plan_id }];
    });
  };

  const removeStudent = (studentId) => {
    setSelectedStudents((prev) => prev.filter((p) => p.student_id !== studentId));
  };

  const handleConfirm = async () => {
    if (submitLoading || selectedStudents.length === 0) return;
    const endNow = subscriptionAction === SUBSCRIPTION_ACTION_END_NOW;
    const end = endNow ? format(subDays(new Date(), 1), DATE_DISPLAY) : String(periodEndDate).trim();
    if (!endNow && !end) {
      setMessage({ type: 'error', text: 'Set subscription end date.' });
      return;
    }
    setSubmitLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await api.post('/data/student-subscriptions/plan-2', {
        studentIds: selectedStudents.map((s) => s.student_id),
        periodEndDate: end,
        endNow,
      });
      setMessage({
        type: 'success',
        text: `Updated ${selectedStudents.length} student(s).`,
      });
      setSelectedStudents([]);
      setPeriodEndDate('');
      setSubscriptionAction(SUBSCRIPTION_ACTION_SET_DATE);
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Request failed',
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="Subscription" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 720, margin: '0 auto' }}>
          {message.text ? <div className={`message ${message.type}`}>{message.text}</div> : null}

          <div className="form-section">
            <h2>Subscription</h2>
            <p style={{ color: '#555', marginTop: 8 }}>
              Search by username, or use #80 for student ID, add students, set subscription end, then
              confirm (plan 2, active from today).
            </p>
          </div>

          <div className="form-section insert-form" style={{ marginBottom: 24 }}>
            <div className="form-group">
              <label>Search student username/#id</label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type username or #student ID..."
                autoComplete="off"
              />
              {searchLoading ? <small>Searching…</small> : null}
              {searchError ? <div className="message error">{searchError}</div> : null}
              {results.length > 0 ? (
                <ul
                  className="sidebar-submenu"
                  style={{ marginTop: 8, maxHeight: 240, overflow: 'auto', listStyle: 'none' }}
                >
                  {results.map((s) => {
                    const added = selectedStudents.some((p) => p.student_id === s.student_id);
                    return (
                      <li
                        key={s.student_id}
                        className="sidebar-item"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                      >
                        <span>
                          #{s.student_id} — {s.username ?? '(no username)'}
                          {s.plan_id != null ? ` — plan ${s.plan_id}` : ''}
                        </span>
                        <button
                          type="button"
                          className="submit-button"
                          style={{ padding: '4px 10px', fontSize: 13 }}
                          disabled={added}
                          onClick={() => addStudent(s)}
                        >
                          {added ? 'Added' : 'Add'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>

            <div className="form-group">
              <label>Students to update</label>
              {selectedStudents.length === 0 ? (
                <p style={{ color: '#888', margin: 0 }}>None selected. Use Add on a search result.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
                  {selectedStudents.map((s) => (
                    <li
                      key={s.student_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 0',
                        borderBottom: '1px solid #eee',
                      }}
                    >
                      <span>
                        #{s.student_id} — {s.username ?? '(no username)'}
                      </span>
                      <button
                        type="button"
                        className="delete-question-button"
                        onClick={() => removeStudent(s.student_id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label>Subscription action</label>
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="subscriptionAction"
                    value={SUBSCRIPTION_ACTION_SET_DATE}
                    checked={subscriptionAction === SUBSCRIPTION_ACTION_SET_DATE}
                    onChange={() => setSubscriptionAction(SUBSCRIPTION_ACTION_SET_DATE)}
                  />{' '}
                  Start
                </label>
                <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="subscriptionAction"
                    value={SUBSCRIPTION_ACTION_END_NOW}
                    checked={subscriptionAction === SUBSCRIPTION_ACTION_END_NOW}
                    onChange={() => setSubscriptionAction(SUBSCRIPTION_ACTION_END_NOW)}
                  />{' '}
                  End
                </label>
              </div>
            </div>

            {subscriptionAction === SUBSCRIPTION_ACTION_SET_DATE ? (
              <div className="form-group">
                <label>
                  Subscription till <span className="required">*</span>
                </label>
                <DatePicker
                  selected={displayStringToDate(periodEndDate)}
                  onChange={(date) => setPeriodEndDate(date ? format(date, DATE_DISPLAY) : '')}
                  dateFormat={DATE_DISPLAY}
                  placeholderText="dd/mm/yyyy"
                  autoComplete="off"
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  dropdownMode="select"
                />
              </div>
            ) : (
              <p style={{ color: '#555', margin: '-8px 0 16px' }}>
                Confirm will set the subscription end date to yesterday.
              </p>
            )}

            <button
              type="button"
              className="submit-button"
              disabled={
                submitLoading ||
                selectedStudents.length === 0 ||
                (subscriptionAction === SUBSCRIPTION_ACTION_SET_DATE &&
                  String(periodEndDate).trim() === '')
              }
              onClick={handleConfirm}
            >
              {submitLoading ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Subscription;
